
import { useState, useEffect, useContext, useRef, useMemo } from "react";
import { useMsal, } from '@azure/msal-react';
import { UserContext } from "../../pages/user/user-settings";
import { useParams } from "react-router-dom";
import moment from 'moment';
import DateHelpers from './../../app/datehelpers';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import UserApi from '../../api/user//user-api-factory';
import { HeartBeatChart } from './heartbeatChart';

import './charts.css';
import { arrayAny } from "../../utils/arrayUtils";

export const shouldFetch = (fetchHistory, start, end, fetchInProgress, userId) => {

    if (!fetchInProgress && start && end && userId) {

        const found = fetchHistory.find(f => f.userId === userId && f.start === start && f.end === end);

        return found ? false : true;
    }

    return false;

};

export const UserActivityTab = () => {

    const { instance, inProgress, accounts } = useMsal();
    const { id } = useParams();
    const { myProfile } = useContext(UserContext);
    const userApi = useMemo(() => new UserApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    const pickerRef = useRef(null);
    const dh = new DateHelpers();

    const [startDate, setStartDate] = useState(moment().startOf('week'));
    const [endDate, setEndDate] = useState(moment().endOf('week'));
    const [rerender, setRerender] = useState(false);

    const [reportData, setReportData] = useState(null);
    const [fetchingReportData, setFetchingReportData] = useState(false);
    const [fetchHistory, setFetchHistory] = useState([]);

    useEffect(() => {
        if (pickerRef.current) {
            pickerRef.current.setStartDate(startDate.toDate());
            pickerRef.current.setEndDate(endDate.toDate());
        }
    }, [startDate, endDate]);

    useEffect(() => {

        // For the current user ("me"/no id), prefer Entra Object Id (GUID)
        const userId = id && id !== 'me'
            ? id
            : (myProfile ?? {}).objectId || (myProfile ?? {}).id;
        const start = new Date(startDate.toDate().setHours(0, 0, 0, 0)).toISOString();
        const end = new Date(endDate.toDate().setHours(23, 59, 59, 999)).toISOString();

        if (shouldFetch(fetchHistory, start, end, fetchingReportData, userId)) {
            setFetchingReportData(true);

            userApi.getHeartBeatReports(userId, start, end)
                .then(data => {
                    setFetchHistory(current => [...current, { userId: userId, start: start, end: end, data: data }]);
                    setReportData(data);
                })
                .catch(err => console.error("An error occurred fetching report data", err))
                .finally(() => setFetchingReportData(false));
        }
        else {
            const record = fetchHistory.find(h => h.userId === userId && h.start === start && h.end === end);
            if (record && record.data) {
                setReportData(record.data);
            }
        }
    }, [id, myProfile, startDate, endDate, userApi, fetchingReportData, fetchHistory]);

    const handleCallback = (start, end) => {
        const now = moment();
        const maxFutureDate = now.add(7, 'days'); // No more than 1 week into future
        if (start.isBefore(maxFutureDate) && end.isBefore(maxFutureDate)) {
            // Ensure the selected range is within 1-week blocks
            const startOfWeek = moment(start).startOf('week');
            const endOfWeek = moment(start).endOf('week');
            if (start.isSame(startOfWeek) && end.isSame(endOfWeek)) {
                setStartDate(start);
                setEndDate(end);
            } else {
                setEndDate(moment(end));
                setStartDate(moment(end).startOf('week'))
            }
        }
    };

    const dateNoFurtherThan7DaysInFuture = date => {
        const now = moment();
        const maxFutureDate = now.add(7, 'days'); // No more than 1 week into future
        return date.isBefore(maxFutureDate);
    };

    const moveBack = () => {
        setStartDate(current => {
            if (dateNoFurtherThan7DaysInFuture(current.subtract(7, 'days'))) {
                return current.subtract(7, 'days');
            }
            return current;
        });

        setEndDate(current => {
            if (dateNoFurtherThan7DaysInFuture(current.subtract(7, 'days'))) {
                return current.subtract(7, 'days');
            }
            return current;
        });


        setRerender(!rerender);
    };

    const moveForward = () => {

        setStartDate(current => {
            if (dateNoFurtherThan7DaysInFuture(current.add(7, 'days'))) {
                return current.subtract(7, 'days');
            }
            return current;
        });

        setEndDate(current => {
            if (dateNoFurtherThan7DaysInFuture(current.add(7, 'days'))) {
                return current.subtract(7, 'days');
            }
            return current;
        });
        setRerender(!rerender);
    };

    return (
        <>
            <div className='row'>
                <div className="offset-xl-5 mb-3">
                    <button className="btn btn-inverse text-truncate" onClick={() => moveBack()}>
                        <i className="fa fa-arrow-left fa-fw text-white"></i>
                    </button>
                    <DateRangePicker
                        ref={pickerRef}
                        startDate={startDate} endDate={endDate}
                        initialSettings={{
                            startDate: startDate.toDate(),
                            endDate: endDate.toDate()
                        }}
                        onApply={(event, picker) => handleCallback(picker.startDate, picker.endDate)} >
                        <button className="btn btn-inverse text-truncate">
                            <i className="fa fa-calendar fa-fw text-white me-1 text-opacity-50"></i>
                            <span>{dh.formatMoment(startDate)} - {dh.formatMoment(endDate)} </span>
                            <b className="caret ms-1 opacity-5"></b>
                        </button>
                    </DateRangePicker>
                    <button className="btn btn-inverse text-truncate" onClick={() => moveForward()}>
                        <i className="fa fa-arrow-right fa-fw text-white"></i>
                    </button>
                </div>
            </div>
            {reportData && arrayAny(Object.entries(reportData)) && Object.entries(reportData).map((dayData, i) => {
                return <div key={`dayChart-${i}`} className='row mb-3'>
                    <HeartBeatChart key={`dayChart-${i}`} series={dayData[1]} day={dayData[0]} />
                </div>
            })}
        </>
    )
};